// LOCK-STEP MIRROR: keep in sync with InviteCloudBackend/src/modules/experience/contracts/dsl.ts
// Any change to the schema body must be applied to both copies simultaneously.
// A breaking change (field removed, type narrowed) requires a version bump in zExperienceDSL.
import { z } from 'zod';

// ─── Shared primitives ───────────────────────────────────────────────────────

export const PLAN_TIERS = ['basic', 'pro', 'premium'] as const;

export const zVenue = z.object({
  name: z.string().min(1),
  // No dedicated "address" field exists in every editor that feeds this DSL
  // (see ExperiencePreview.resolveExperienceManifest) — allow empty rather
  // than reject a manifest that legitimately has a venue name but no address.
  address: z.string(),
  mapsLink: z.string().url().optional(),
});

export const zEmotion = z.object({
  primary: z.enum(['romantic', 'festive', 'sacred', 'celebratory']),
  intensity: z.enum(['subtle', 'moderate', 'dramatic']).default('moderate'),
});

// ─── DSL v1.0 (wedding-only — FROZEN, never modify) ─────────────────────────

export const RITUAL_TYPES = ['engagement', 'haldi', 'antarpat', 'varmala', 'saptapadi'] as const;
// 'heritage' added 2026-08-08: a T15 style pack (see style-packs.ts) that was fully built —
// colors, fonts, vignette — but never added here, so the one wedding template using it
// (exp-wedding-rajasthani-royal-v1) always failed validation with "template: Invalid option".
export const TEMPLATE_STYLES = ['royal', 'minimal', 'fantasy', 'premium_thematic', 'modern_vibrant', 'heritage', 'indo_ghibli', 'pichwai_heritage', 'storybook_3d'] as const;

export const zRitualScene = z.object({
  ritualId: z.enum(RITUAL_TYPES),
  order: z.number().int().min(0),
  date: z.string(),
  time: z.string().optional(),
  venue: zVenue,
  emotionalArc: zEmotion.default({ primary: 'festive', intensity: 'moderate' }),
  customNote: z.string().max(280).optional(),
  dressCode: z.string().max(120).optional(),
});

export const zExperienceMeta = z.object({
  brideName: z.string().min(1),
  groomName: z.string().min(1),
  weddingDate: z.string(),
  tagline: z.string().max(140).optional(),
  referenceImages: z.object({
    bride: z.string().url().optional(),
    groom: z.string().url().optional(),
    couple: z.string().url().optional(),
  }).default({}),
});

export const zExperienceDSL_v10 = z.object({
  version: z.literal('1.0'),
  template: z.enum(TEMPLATE_STYLES),
  plan: z.enum(PLAN_TIERS),
  meta: zExperienceMeta,
  rituals: z.array(zRitualScene).min(1).max(8)
    .refine(rs => new Set(rs.map(r => r.ritualId)).size === rs.length, { message: 'duplicate ritualId' })
    .refine(rs => new Set(rs.map(r => r.order)).size === rs.length, { message: 'duplicate order' }),
});

// ─── DSL v1.1 (multi-occasion — generic "moments") ──────────────────────────

export const OCCASIONS = [
  'wedding', 'birthday', 'anniversary', 'send_off',
  'baby_shower', 'engagement', 'custom',
] as const;
export type Occasion = typeof OCCASIONS[number];

export const MOMENT_TYPES = {
  wedding:     ['engagement', 'haldi', 'antarpat', 'varmala', 'saptapadi'],
  birthday:    ['countdown', 'story', 'cake_moment', 'memories', 'party'],
  anniversary: ['then_and_now', 'milestones', 'vow_renewal', 'toast'],
  send_off:    ['journey', 'memories', 'wishes', 'farewell_toast'],
  baby_shower: ['announcement', 'nursery_reveal', 'blessings', 'games'],
  engagement:  ['proposal_story', 'ring_reveal', 'families', 'celebration'],
  custom:      ['opening', 'story', 'highlight', 'gallery', 'closing'],
} as const satisfies Record<Occasion, readonly string[]>;

export type MomentType = typeof MOMENT_TYPES[Occasion][number];

export const zMomentScene = z.object({
  momentId: z.string().min(1),
  order: z.number().int().min(0),
  date: z.string(),
  time: z.string().optional(),
  venue: zVenue.optional(),
  emotionalArc: zEmotion.default({ primary: 'festive', intensity: 'moderate' }),
  customNote: z.string().max(280).optional(),
  dressCode: z.string().max(120).optional(),
});

export const zExperienceMetaV11 = z.object({
  occasion: z.enum(OCCASIONS),
  primaryName: z.string().min(1),
  secondaryName: z.string().min(1).optional(),
  eventDate: z.string(),
  tagline: z.string().max(140).optional(),
  referenceImages: z.object({
    primary: z.string().url().optional(),
    secondary: z.string().url().optional(),
    together: z.string().url().optional(),
  }).default({}),
}).superRefine((meta, ctx) => {
  const requiresSecondary: Occasion[] = ['wedding', 'anniversary', 'engagement'];
  if (requiresSecondary.includes(meta.occasion) && !meta.secondaryName) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      // Without an explicit path this issue lands at ['meta'] (the object being refined),
      // not ['meta', 'secondaryName'] — ExperienceDetailsStep's friendlyDslErrors() matches
      // on the LAST path segment to map a field to human copy, so a missing path here made
      // the friendly mapping a silent no-op for anniversary/engagement, falling through to
      // this raw message instead. Found in code review 2026-08-08.
      path: ['secondaryName'],
      message: `secondaryName is required for occasion '${meta.occasion}'`,
    });
  }
});

export const zExperienceDSL_v11 = z.object({
  version: z.literal('1.1'),
  template: z.string().min(1),
  plan: z.enum(PLAN_TIERS),
  meta: zExperienceMetaV11,
  moments: z.array(zMomentScene).min(1).max(10)
    .superRefine((moments, ctx) => {
      const orders = new Set(moments.map(m => m.order));
      if (orders.size !== moments.length) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'duplicate order in moments' });
      }
    }),
}).superRefine((dsl, ctx) => {
  const allowedMoments = MOMENT_TYPES[dsl.meta.occasion] as readonly string[];
  for (const m of dsl.moments) {
    if (!allowedMoments.includes(m.momentId)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `momentId '${m.momentId}' is not valid for occasion '${dsl.meta.occasion}'. Allowed: ${allowedMoments.join(', ')}`,
      });
    }
  }
});

// ─── Union — the public parse surface ────────────────────────────────────────

export const zExperienceDSL = z.union([zExperienceDSL_v10, zExperienceDSL_v11]);

// ─── Upgrade path: v1.0 → v1.1 (pure, no side effects) ──────────────────────

export type ExperienceDSLV10 = z.infer<typeof zExperienceDSL_v10>;
export type ExperienceDSLV11 = z.infer<typeof zExperienceDSL_v11>;
export type ExperienceDSL = z.infer<typeof zExperienceDSL>;

export function upgradeDSL(dsl: ExperienceDSL): ExperienceDSLV11 {
  if (dsl.version === '1.1') return dsl;
  return {
    version: '1.1',
    template: dsl.template,
    plan: dsl.plan,
    meta: {
      occasion: 'wedding',
      primaryName: dsl.meta.brideName,
      secondaryName: dsl.meta.groomName,
      eventDate: dsl.meta.weddingDate,
      tagline: dsl.meta.tagline,
      referenceImages: {
        primary: dsl.meta.referenceImages.bride,
        secondary: dsl.meta.referenceImages.groom,
        together: dsl.meta.referenceImages.couple,
      },
    },
    moments: dsl.rituals.map(r => ({
      momentId: r.ritualId,
      order: r.order,
      date: r.date,
      time: r.time,
      venue: r.venue,
      emotionalArc: r.emotionalArc,
      customNote: r.customNote,
      dressCode: r.dressCode,
    })),
  };
}

// Legacy type aliases for back-compat
export type RitualType = typeof RITUAL_TYPES[number];
export type TemplateStyle = typeof TEMPLATE_STYLES[number];
export type PlanTier = typeof PLAN_TIERS[number];
